/**
 * Google Identity Services, loaded on demand.
 *
 * The script comes from Google (it is what renders the official button and runs the account picker), so
 * it is added to the page only when the service says a Google client is configured — a password-only
 * deployment never talks to Google at all. What we get back is the ID token (`credential`); the service
 * verifies it and decides. Nothing here trusts the token's contents.
 */

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

interface GoogleId {
  initialize(config: {
    client_id: string
    callback: (res: { credential: string }) => void
    hosted_domain?: string
    ux_mode?: 'popup' | 'redirect'
  }): void
  renderButton(parent: HTMLElement, options: Record<string, string | number>): void
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleId } }
  }
}

let loading: Promise<GoogleId> | null = null

export function loadGoogleIdentity(): Promise<GoogleId> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id)
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.onload = () => {
        const id = window.google?.accounts?.id
        if (id) resolve(id)
        else reject(new Error('Google Identity Services did not initialise'))
      }
      script.onerror = () => {
        loading = null
        reject(new Error('could not load Google Identity Services'))
      }
      document.head.appendChild(script)
    })
  }
  return loading
}

/** Draw Google's own button into `parent` and hand every completed sign-in's ID token to `onCredential`. */
export async function renderGoogleButton(
  parent: HTMLElement,
  clientId: string,
  hostedDomain: string,
  locale: string,
  onCredential: (credential: string) => void,
): Promise<void> {
  const id = await loadGoogleIdentity()
  id.initialize({ client_id: clientId, callback: (res) => onCredential(res.credential), hosted_domain: hostedDomain, ux_mode: 'popup' })
  parent.replaceChildren()
  id.renderButton(parent, { type: 'standard', theme: 'outline', size: 'large', text: 'signin_with', width: 320, locale })
}
